import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Hexagon, Phone, Globe, Lock, User, ArrowRight, CheckCircle2, MapPin, MessageCircle, Info } from 'lucide-react';

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const res = await login(username, password);
            if (res.success) {
                navigate('/');
            } else {
                setError(res.error);
            }
        } catch (err) {
            setError('Bağlantı hatası oluştu.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4 font-sans">
            <div className="max-w-4xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
                
                {/* Left Side - Hero / Branding */}
                <div className="md:w-1/2 bg-gradient-to-br from-amber-500 to-amber-600 p-8 text-white flex flex-col justify-between relative overflow-hidden">
                    {/* Abstract Shapes */}
                    <div className="absolute top-0 left-0 w-full h-full opacity-10">
                        <Hexagon size={200} className="absolute -top-10 -left-10 text-white animate-pulse" />
                        <Hexagon size={100} className="absolute bottom-10 right-10 text-white" />
                    </div>

                    <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                                <Hexagon size={32} fill="white" className="text-amber-600" />
                            </div>
                            <h1 className="text-2xl font-bold tracking-wide">KovanTakip</h1>
                        </div>
                        
                        <div className="space-y-4 mb-8">
                            <h2 className="text-3xl font-bold leading-tight">Arıcılık Yönetiminde Profesyonel Çözüm</h2>
                            <p className="text-amber-100 text-sm">Kovanlarınızı, hasatlarınızı ve arı sağlığını tek bir yerden kolayca takip edin.</p>
                            <a href="/tanitim.html" target="_blank" className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg text-sm font-bold transition backdrop-blur-sm">
                                <Info size={16} /> Sistemi Keşfet / Tanıtım
                            </a>
                        </div>

                        <div className="space-y-2 text-sm text-amber-50 font-medium">
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-green-300" />
                                <span>Detaylı Kovan Takibi</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-green-300" />
                                <span>QR Kod ile Hızlı İşlem</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={16} className="text-green-300" />
                                <span>Verimlilik Analizleri</span>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-10 mt-8 pt-6 border-t border-white/20">
                        <p className="text-xs uppercase tracking-wider opacity-70 mb-2">Geliştirici & Destek</p>
                        <h3 className="font-bold text-lg flex items-center gap-2">
                            3B Bilgisayar Yazılım
                        </h3>
                        <div className="mt-3 space-y-2 text-sm text-amber-100">
                            <div className="flex items-center gap-2">
                                <Phone size={14} className="shrink-0" /> 0505 223 26 57
                            </div>
                            <div className="flex items-start gap-2">
                                <MapPin size={14} className="mt-1 shrink-0" />
                                <span>Yeni Mahalle Milli Egemenlik Caddesi No 17/C Araç/Kastamonu</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Globe size={14} className="shrink-0" /> www.3bbilgisayar.com
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Login Form */}
                <div className="md:w-1/2 p-8 md:p-12 bg-white flex flex-col justify-center">
                    <div className="text-center md:text-left mb-8">
                        <h2 className="text-2xl font-bold text-gray-800">Hoş Geldiniz</h2>
                        <p className="text-gray-500 text-sm mt-1">Hesabınıza giriş yaparak kovanlarınızı yönetmeye başlayın.</p>
                    </div>

                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Kullanıcı Adı</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 transition sm:text-sm"
                                    placeholder="Kullanıcı adınız"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-sm font-medium text-gray-700">Şifre</label>
                            </div>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock size={18} className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 transition sm:text-sm"
                                    placeholder="Şifreniz"
                                    required
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Giriş Yapılıyor...' : 'Giriş Yap'}
                            {!loading && <ArrowRight size={18} className="ml-2" />}
                        </button>
                    </form>

                    <div className="mt-8 text-center border-t pt-6">
                        <p className="text-sm text-gray-600 mb-2">Henüz hesabınız yok mu?</p>
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-left">
                            <h4 className="font-bold text-blue-800 text-sm mb-1">Abonelik & Kayıt İçin:</h4>
                            <p className="text-xs text-blue-600 mb-2">Yeni üyelik işlemleri ve paket detayları için lütfen yöneticinizle iletişime geçin.</p>
                            <div className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                                <span className="flex items-center gap-1"><User size={12}/> İletişim: 3B Yazılım Destek</span>
                                <span className="flex items-center gap-1"><Phone size={12}/> Tel: 0505 223 26 57</span>
                            </div>
                            <div className="mt-3">
                                <a 
                                    href="https://wa.me/905052232657?text=Merhaba,%20KovanTakip%20abonelik%20hakkında%20bilgi%20almak%20istiyorum." 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 w-full bg-green-500 text-white p-2 rounded-lg font-bold hover:bg-green-600 transition text-xs"
                                >
                                    <MessageCircle size={16} /> WhatsApp ile İletişime Geç
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Footer */}
            <div className="fixed bottom-4 text-center w-full text-xs text-gray-400 pointer-events-none">
                &copy; {new Date().getFullYear()} 3B Bilgisayar Yazılım. Tüm hakları saklıdır.
            </div>
        </div>
    );
}
